import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Calendar, MapPin, Ticket, ShieldCheck, Loader2 } from "lucide-react";

const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicTicketing() {
  const { slug, eventId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [selectedCat, setSelectedCat] = useState("");
  const [qty, setQty] = useState(1);
  const [buyer, setBuyer] = useState({ name: "", email: "", phone: "" });
  const [submitting, setSubmitting] = useState(false);
  const [formErr, setFormErr] = useState(null);

  useEffect(() => {
    axios.get(`${apiBase}/public/ticketing/${slug}/${eventId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.response?.data?.detail || "Événement introuvable"))
      .finally(() => setLoading(false));
  }, [slug, eventId]);

  const submit = async (e) => {
    e.preventDefault();
    setFormErr(null);
    if (!selectedCat) { setFormErr("Choisissez une catégorie"); return; }
    if (!buyer.name || !buyer.email || !buyer.phone) { setFormErr("Tous les champs sont requis"); return; }
    setSubmitting(true);
    try {
      const { data: resp } = await axios.post(
        `${apiBase}/public/ticketing/${slug}/${eventId}/checkout`,
        {
          category_id: selectedCat,
          quantity: parseInt(qty, 10) || 1,
          buyer_name: buyer.name,
          buyer_email: buyer.email,
          buyer_phone: buyer.phone,
        },
      );
      window.location.href = resp.payment_url;
    } catch (er) {
      setFormErr(er.response?.data?.detail || "Erreur lors de l'initialisation du paiement");
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#0A4628] flex items-center justify-center text-white">
      <Loader2 className="w-8 h-8 animate-spin" />
    </div>
  );
  if (err) return (
    <div className="min-h-screen bg-[#0A4628] flex items-center justify-center text-white p-6">
      <div className="bg-[#0A4628]/40 border border-white/10 rounded-lg p-8 max-w-md text-center">
        <p className="text-lg font-semibold mb-2">Oups</p>
        <p className="text-white/70 text-sm">{err}</p>
      </div>
    </div>
  );

  const { club, event } = data;
  const primary = club.primary_color || "#9E1B1B";
  const fmt = (n) => `${(n || 0).toLocaleString("fr-FR")} FCFA`;
  const cat = (event.categories || []).find((c) => c.id === selectedCat);
  const total = cat ? cat.price_xof * (parseInt(qty, 10) || 1) : 0;

  return (
    <div className="min-h-screen bg-[#F4F5F7]" data-testid="public-ticketing-page">
      {/* Hero */}
      <div className="relative" style={{ background: `linear-gradient(135deg, ${primary} 0%, #121212 100%)` }}>
        {event.image_url && (
          <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${event.image_url})` }} />
        )}
        <div className="relative max-w-5xl mx-auto px-4 lg:px-8 py-10 lg:py-16 text-white">
          <p className="text-xs tracking-[0.18em] uppercase opacity-80 mb-3">Billetterie officielle · {club.name}</p>
          <h1 className="font-display text-3xl lg:text-5xl font-extrabold mb-3">{event.name}</h1>
          <div className="flex flex-wrap gap-4 text-sm opacity-90">
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{new Date(event.starts_at).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}</span>
            {event.venue && <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{event.venue}</span>}
          </div>
          {event.description && <p className="mt-4 text-white/85 max-w-2xl text-sm">{event.description}</p>}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Categories */}
        <div className="lg:col-span-2 space-y-3" data-testid="categories-list">
          <h2 className="font-display text-xl font-bold mb-2">Choisissez votre billet</h2>
          {event.categories.map((c) => {
            const isOpen = c.remaining > 0;
            const isSel = selectedCat === c.id;
            return (
              <button
                key={c.id}
                type="button"
                disabled={!isOpen}
                onClick={() => setSelectedCat(c.id)}
                className={`w-full text-left p-4 rounded-lg border transition ${
                  isSel ? "border-2 shadow-md" : "border border-[#E0E0E0] hover:border-[#9E9E9E]"
                } ${!isOpen ? "opacity-50 cursor-not-allowed" : "bg-white"}`}
                style={isSel ? { borderColor: primary } : {}}
                data-testid={`category-${c.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-display font-bold text-lg">{c.name}</p>
                    {c.description && <p className="text-xs text-[#616161] mt-0.5">{c.description}</p>}
                    <p className="text-[11px] text-[#616161] mt-1">
                      {isOpen ? `${c.remaining} place${c.remaining > 1 ? "s" : ""} disponible${c.remaining > 1 ? "s" : ""}` : "Épuisé"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-xl font-extrabold" style={{ color: primary }}>{fmt(c.price_xof)}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Form */}
        <div className="bg-white border border-[#E0E0E0] rounded-lg p-5 h-fit sticky top-6">
          <h3 className="font-display font-bold text-lg mb-3">Vos informations</h3>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Nom complet *</Label>
              <Input required value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} data-testid="buyer-name" />
            </div>
            <div>
              <Label>Email *</Label>
              <Input required type="email" value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} data-testid="buyer-email" />
            </div>
            <div>
              <Label>Téléphone *</Label>
              <Input required value={buyer.phone} onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })} placeholder="+229XXXXXXXX" data-testid="buyer-phone" />
              <p className="text-[10px] text-[#616161] mt-1">Format international (+229, +225, +228…)</p>
            </div>
            <div>
              <Label>Quantité *</Label>
              <Input required type="number" min="1" max="20" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="qty" />
            </div>

            {cat && (
              <div className="border-t border-[#E0E0E0] pt-3 mt-3">
                <div className="flex justify-between text-sm">
                  <span>{cat.name} × {qty || 1}</span>
                  <span className="font-mono">{fmt(cat.price_xof * (parseInt(qty, 10) || 1))}</span>
                </div>
                <div className="flex justify-between text-lg font-bold mt-1" style={{ color: primary }}>
                  <span>Total</span>
                  <span className="font-mono">{fmt(total)}</span>
                </div>
              </div>
            )}

            {!selectedCat && (
              <div className="bg-[#FBC02D]/15 border border-[#FBC02D]/40 rounded p-2 text-xs text-[#F57F17] flex items-center gap-1.5" data-testid="form-warn-cat">
                <Ticket className="w-3.5 h-3.5" /> Choisissez d&apos;abord une catégorie de billet ci-contre
              </div>
            )}

            {formErr && (
              <div className="bg-[#C62828]/10 border border-[#C62828]/30 rounded p-2 text-xs text-[#C62828]" data-testid="form-error">
                {formErr}
              </div>
            )}

            <Button type="submit" disabled={submitting || !selectedCat} className="w-full text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: primary }} data-testid="pay-btn">
              {submitting
                ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirection…</>)
                : !selectedCat
                  ? (<><Ticket className="w-4 h-4 mr-2" /> Sélectionnez une catégorie</>)
                  : (<><Ticket className="w-4 h-4 mr-2" /> Payer {fmt(total)} avec FedaPay</>)
              }
            </Button>

            <div className="flex items-center gap-2 text-[11px] text-[#616161] justify-center mt-2">
              <ShieldCheck className="w-3.5 h-3.5" /> Paiement sécurisé Mobile Money & cartes bancaires
            </div>
          </form>
        </div>
      </div>

      <footer className="text-center text-xs text-[#9E9E9E] py-6 border-t border-[#E0E0E0]">
        Propulsé par <strong className="text-[#0A4628]">WSDK Connect</strong> · {club.name}
      </footer>
    </div>
  );
}
